import { DateTime } from 'luxon';
import { z } from 'zod';
import {
  registrationProfileAddressSchema,
  type EligibilityCheckRequest,
  type RegistrationProfileAddress,
  type RegistrationProfileWrite,
  type SexAssignedAtBirth,
} from '@galapagos/shared';
import { stateFromZip, US_STATES } from '@galapagos/shared/registration';
import type { OtpChannel } from '@galapagos/shared/api/auth';
import { planDetailsWrite, type SignupPlanDetails } from '../health-plan/plan-details';
import type { SignupLegalName } from '../stores/signup-flow-store';

/** The address step's fields as typed, before trimming. */
export interface AddressDraft {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

export const EMPTY_ADDRESS_DRAFT: AddressDraft = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
};

export type AddressErrorKey =
  | 'address.line1Error'
  | 'address.cityError'
  | 'address.stateError'
  | 'address.zipError'
  | 'address.zipLengthError'
  | 'address.tooLongError';

export interface AddressErrors {
  line1?: AddressErrorKey;
  line2?: AddressErrorKey;
  city?: AddressErrorKey;
  state?: AddressErrorKey;
  zip?: AddressErrorKey;
}

function maxLengthOf(schema: { maxLength: number | null }): number {
  if (schema.maxLength === null) throw new Error('the profile address schema lost a length limit');
  return schema.maxLength;
}

/** The profile save's own limits, so the address step refuses what the save would reject. */
export const ADDRESS_MAX_LENGTH = {
  line1: maxLengthOf(registrationProfileAddressSchema.shape.line1),
  line2: maxLengthOf(registrationProfileAddressSchema.shape.line2.unwrap()),
  city: maxLengthOf(registrationProfileAddressSchema.shape.city),
} as const;

const US_STATE_CODES: ReadonlySet<string> = new Set(US_STATES.map((state) => state.code));
const FIVE_DIGIT_ZIP = /^\d{5}$/;

export function validateAddress(draft: AddressDraft): AddressErrors {
  const errors: AddressErrors = {};
  const line1 = draft.line1.trim();
  const city = draft.city.trim();
  if (!line1) errors.line1 = 'address.line1Error';
  else if (line1.length > ADDRESS_MAX_LENGTH.line1) errors.line1 = 'address.tooLongError';
  if (draft.line2.trim().length > ADDRESS_MAX_LENGTH.line2) errors.line2 = 'address.tooLongError';
  if (!city) errors.city = 'address.cityError';
  else if (city.length > ADDRESS_MAX_LENGTH.city) errors.city = 'address.tooLongError';
  if (!US_STATE_CODES.has(draft.state)) errors.state = 'address.stateError';
  const zip = draft.zip.trim();
  if (!zip) errors.zip = 'address.zipError';
  else if (!FIVE_DIGIT_ZIP.test(zip)) errors.zip = 'address.zipLengthError';
  return errors;
}

export function hasAddressErrors(errors: AddressErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** Production's ZIP rule: digits only, the first five kept, so a pasted ZIP+4 works. */
export function keepZipDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 5);
}

/** The address step's form: the fields, plus which state (if any) the ZIP filled in. */
export interface AddressForm {
  draft: AddressDraft;
  zipFilledState: string | null;
}

export const EMPTY_ADDRESS_FORM: AddressForm = { draft: EMPTY_ADDRESS_DRAFT, zipFilledState: null };

/**
 * Production's ZIP rule and its state fill, as one pure step: the ZIP keeps
 * its first five digits, and a full ZIP fills the state while the member has
 * not chosen one and the state is empty or still the one a ZIP filled.
 */
export function withZip(form: AddressForm, typed: string, stateChosen: boolean): AddressForm {
  const zip = keepZipDigits(typed);
  const zipState = stateFromZip(zip);
  const { draft, zipFilledState } = form;
  if (
    zipState === undefined ||
    stateChosen ||
    (draft.state !== '' && draft.state !== zipFilledState)
  ) {
    return { ...form, draft: { ...draft, zip } };
  }
  return { draft: { ...draft, zip, state: zipState }, zipFilledState: zipState };
}

/** The saved shape: trimmed, with an empty second line left out. */
export function toProfileAddress(draft: AddressDraft): RegistrationProfileAddress {
  const line2 = draft.line2.trim();
  return {
    line1: draft.line1.trim(),
    ...(line2 ? { line2 } : {}),
    city: draft.city.trim(),
    state: draft.state,
    zip: draft.zip.trim(),
  };
}

export interface BirthDateErrors {
  date?: 'birthDate.dateError' | 'birthDate.futureError';
  sex?: 'birthDate.sexError';
}

/**
 * Today's calendar day in UTC, the same "not in the future" rule the
 * eligibility schema applies. A birth date has no member zone yet.
 */
export function utcToday(now: DateTime = DateTime.now()): string | null {
  return now.setZone('utc').toISODate();
}

export function validateBirthDate(
  isoDate: string | null,
  sex: SexAssignedAtBirth | null,
  today: string | null = utcToday(),
): BirthDateErrors {
  const errors: BirthDateErrors = {};
  if (isoDate === null) errors.date = 'birthDate.dateError';
  else if (today !== null && isoDate > today) errors.date = 'birthDate.futureError';
  if (sex === null) errors.sex = 'birthDate.sexError';
  return errors;
}

/** The contact the member has not given yet: the other channel from sign-in. */
/**
 * How the member signed in: the typed contact, or for an employer SSO member
 * only the channel, since the roster contact never reaches the browser.
 */
export interface SignedInWith {
  channel: OtpChannel;
  destination?: string;
}

export function contactToAsk(signInContact: SignedInWith | null): 'email' | 'phone' {
  return signInContact?.channel === 'email' ? 'phone' : 'email';
}

const emailSchema = z.string().trim().email().max(254);

export function isValidEmail(value: string): boolean {
  return emailSchema.safeParse(value).success;
}

/** Ten US national digits as E.164, or null when the number is incomplete. */
export function toE164UsPhone(digits: string): string | null {
  return /^\d{10}$/.test(digits) ? `+1${digits}` : null;
}

export function buildEligibilityRequest(input: {
  legalName: SignupLegalName;
  dateOfBirth: string;
  zip?: string;
  clientRef?: string;
}): EligibilityCheckRequest {
  return {
    firstName: input.legalName.firstName,
    lastName: input.legalName.lastName,
    dateOfBirth: input.dateOfBirth,
    ...(input.zip ? { zip: input.zip } : {}),
    ...(input.clientRef ? { clientRef: input.clientRef } : {}),
  };
}

/**
 * The profile save from every answer so far. The contact just entered is the
 * channel the member did not sign in with (`enteredContact` is the email text
 * or the ten phone digits). Null when any answer is missing or incomplete, so
 * a partial profile is never sent.
 */
export function buildProfileWrite(input: {
  legalName: SignupLegalName | null;
  signInContact: SignedInWith | null;
  enteredContact: string;
  address: RegistrationProfileAddress | null;
  sexAssignedAtBirth: SexAssignedAtBirth | null;
  /** Health-plan members only; the write carries no plan details key otherwise. */
  planDetails?: SignupPlanDetails | null;
  /** Optional identity details; each key is sent only when the member gave one. */
  genderIdentity?: string | null;
  pronouns?: string | null;
}): RegistrationProfileWrite | null {
  const { legalName, signInContact, address, sexAssignedAtBirth } = input;
  if (!legalName || !signInContact || !address || !sexAssignedAtBirth) return null;
  const planDetails = planDetailsWrite(input.planDetails ?? null);
  const base = {
    firstName: legalName.firstName,
    lastName: legalName.lastName,
    address,
    sexAssignedAtBirth,
    ...(planDetails ? { planDetails } : {}),
    ...(input.genderIdentity ? { genderIdentity: input.genderIdentity } : {}),
    ...(input.pronouns ? { pronouns: input.pronouns } : {}),
  };
  // The sign-in channel is already verified on the account, so a roster
  // sign-in that never saw its destination sends only the entered contact.
  const signedIn = signInContact.destination;
  if (contactToAsk(signInContact) === 'email') {
    const email = input.enteredContact.trim();
    if (!isValidEmail(email)) return null;
    return signedIn === undefined ? { ...base, email } : { ...base, email, phone: signedIn };
  }
  const phone = toE164UsPhone(input.enteredContact);
  if (!phone) return null;
  return signedIn === undefined ? { ...base, phone } : { ...base, email: signedIn, phone };
}
